/* Galleryfield.jsx - USING ImageBitmap API (NO CORS ERRORS) */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { AiFillEdit, AiOutlineClose, AiFillSetting } from "react-icons/ai";
import UploadControlsV2 from "../TWG/UploadControlsV2";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import ReactModal from "react-modal";
import { ImCross } from "react-icons/im";
import Cropper from "react-easy-crop";
import { confirmAlert } from "react-confirm-alert";
import ConfirmToast from "../../components/universal/ConfirmToast"; // adjust path if needed
import LoaderSpinner from "../../components/universal/LoaderSpinner"
import {
  saveGalleryRequest,
  readGalleryRequest,
  deleteGalleryRequest,
  updateGalleryImageRequest,
  getImageBase64Request
} from "../../store/Gallery/galleryAction";
import { uploadAssestsV2Action } from "../../store/Languages/LanguageAction";

/* ---------------------- ImageBitmap CROPPING (NO CORS ERRORS) --------------------- */
async function getCroppedImg(imageSrc, pixelCrop) {
  try {

    // Method 1: Try to fetch as blob first (respects CORS)
    const response = await fetch(imageSrc, {
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Accept': 'image/*'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();

    // Method 2: Create ImageBitmap from blob (NO canvas tainting!)
    const imageBitmap = await createImageBitmap(blob);

    // Method 3: Create offscreen canvas for cropping
    const offscreenCanvas = new OffscreenCanvas(
      Math.max(1, Math.floor(pixelCrop.width)),
      Math.max(1, Math.floor(pixelCrop.height))
    );
    const ctx = offscreenCanvas.getContext('2d');

    if (!ctx) {
      throw new Error("Failed to get 2d context");
    }

    // Clear canvas
    ctx.clearRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);

    // Draw the cropped portion
    ctx.drawImage(
      imageBitmap,
      Math.floor(pixelCrop.x),
      Math.floor(pixelCrop.y),
      Math.floor(pixelCrop.width),
      Math.floor(pixelCrop.height),
      0,
      0,
      offscreenCanvas.width,
      offscreenCanvas.height
    );


    // Method 4: Convert to blob (NO CORS restrictions with OffscreenCanvas!)
    const croppedBlob = await offscreenCanvas.convertToBlob({
      type: 'image/jpeg',
      quality: 0.9
    });


    const url = URL.createObjectURL(croppedBlob);

    // Clean up ImageBitmap
    imageBitmap.close();

    return { blob: croppedBlob, url };

  } catch (error) {

    // Fallback: Try traditional canvas (will fail if CORS)

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.floor(pixelCrop.width));
          canvas.height = Math.max(1, Math.floor(pixelCrop.height));
          const ctx = canvas.getContext("2d");

          ctx.drawImage(
            img,
            Math.floor(pixelCrop.x),
            Math.floor(pixelCrop.y),
            Math.floor(pixelCrop.width),
            Math.floor(pixelCrop.height),
            0,
            0,
            canvas.width,
            canvas.height
          );

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Canvas is empty"));
                return;
              }
              const url = URL.createObjectURL(blob);
              resolve({ blob, url });
            },
            "image/jpeg",
            0.9
          );
        } catch (drawError) {
          reject(new Error("Canvas fallback failed: " + drawError.message));
        }
      };

      img.onerror = () => reject(new Error("Failed to load image in fallback"));

      // Add cache busting for CORS
      const separator = imageSrc.includes('?') ? '&' : '?';
      img.src = imageSrc + separator + 't=' + Date.now();
    });
  }
}

/* ----------------------------- Galleryfield Component ------------------------------ */
const Galleryfield = ({ church }) => {
  const dispatch = useDispatch();
  const { loading, galleryList } = useSelector((state) => state.gallery || {});
  const [isLoadingForEdit, setIsLoadingForEdit] = useState(false);
  const [originalImages, setOriginalImages] = useState({ desktop: "", tablet: "", mobile: "" });

  const [resetUploadKey, setResetUploadKey] = useState(0);
  const [imageName, setImageName] = useState("");
  const [tempUrl, setTempUrl] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [thumbUrls, setThumbUrls] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);

  // Cropping states
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState("desktop");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  // Final URLs
  const [croppedImages, setCroppedImages] = useState({
    desktop: "",
    tablet: "",
    mobile: "",
  });

  // Refs
  const originalBlobRef = useRef(null);

  // Get object IDs
  const getFinalIds = () => {
    let key = "churchObjId";
    let value = church?._id;
    const url = window.location.href.toLowerCase();

    if (url.includes("addchannelfield")) {
      key = "channelObjId";
      value = church?.channelObjId || church?._id;
    } else if (url.includes("addministryfield")) {
      key = "ministriesObjId";
      value = church?.ministriesObjId || church?._id;
    }

    return { finalObjIdKey: key, finalObjIdValue: value };
  };

  /* ---------------------- Fetch gallery list ---------------------- */
  useEffect(() => {
    const { finalObjIdKey, finalObjIdValue } = getFinalIds();
    if (finalObjIdValue) {
      dispatch(readGalleryRequest({ [finalObjIdKey]: finalObjIdValue }));
    }
  }, [church?._id, resetUploadKey]);

  useEffect(() => {
    if (!galleryList) return;
    setThumbUrls(
      galleryList.map((item) => ({
        _id: item._id,
        web: {
          url: item.imageUrl || item.url_link || "",
          title: item.imageName || "Gallery Image",
        },
        mobile: {
          url: item.mobileImageUrl || "",
          title: item.imageName || "Gallery Image",
        },
        tablet: {
          url: item.tabImageUrl || "",
          title: item.imageName || "Gallery Image",
        },
        raw: item,
      }))
    );
  }, [galleryList]);

  /* ---------------------- Load Image as Blob for Editing ---------------------- */
  const loadImageAsBlob = async (imageUrl) => {
    try {

      // Try to fetch with CORS
      const response = await fetch(imageUrl, {
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'image/*'
        }
      });

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);


      return { blob, blobUrl };

    } catch (error) {
            throw error;
    }
  };

  /* ---------------------- Handle File Upload ---------------------- */
  const handleGalleryFileInputChange = (file) => {
    if (!file) return;

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG and PNG images are allowed!");
      return;
    }

    // Reset editing
    setEditingIndex(null);
    // setImageName("");

    // Create blob URL
    const blobUrl = URL.createObjectURL(file);

    // Store original blob
    originalBlobRef.current = { blob: file, url: blobUrl };

    // Set for cropping
    setSelectedImage({
      url: blobUrl,
      previewOnly: false,
      file: file
    });

    // Reset crops
    setCroppedImages({ desktop: "", tablet: "", mobile: "" });
    setActiveTab("desktop");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);

    // Upload original image
    const formData = new FormData();
    formData.append("file", file);

    dispatch(
      uploadAssestsV2Action({
        apiPayloadRequest: formData,
        uploadProgressId: "galleryImageUpload",
        callback: (res) => {
          if (res?.url) {
            setUploadedUrl(res.url);
            setTempUrl(res.url);

            // Set same URL for all devices initially
            setCroppedImages({
              desktop: res.url,
              tablet: res.url,
              mobile: res.url,
            });

            // toast.success("Image uploaded! Now you can crop for different devices.");
          }
        },
      })
    );
  };

  /* ---------------------- Handle URL Input ---------------------- */
  const handleUrlFieldChange = async (e) => {
    const url = typeof e === "string" ? e : e?.target?.value;

    if (!url) return;


    // Validate extension
    const allowedExtensions = [".jpg", ".jpeg", ".png"];
    const urlLower = url.toLowerCase();
    const isValid = allowedExtensions.some((ext) =>
      urlLower.endsWith(ext)
    );

    if (!isValid) {
      toast.error("Only JPG and PNG image URLs are allowed!");
      return;
    }

    // Reset editing state
    setEditingIndex(null);
    // setImageName("");
    setTempUrl(url);

    try {
      // toast.info("Loading image...");

      const { blob, blobUrl } = await loadImageAsBlob(url);

      originalBlobRef.current = { blob, url: blobUrl };

      setSelectedImage({
        url: blobUrl,
        previewOnly: false,
      });

      setCroppedImages({
        desktop: url,
        tablet: url,
        mobile: url,
      });

      setUploadedUrl(url);

      // toast.success("Image loaded for cropping!");
    } catch (error) {

      setSelectedImage({
        url,
        previewOnly: true,
      });

      setCroppedImages({
        desktop: url,
        tablet: url,
        mobile: url,
      });

      setUploadedUrl(url);

      toast.warning(
        "Image loaded (view only). For cropping, please upload the file directly."
      );
    }
  };


  /* ---------------------- Handle Edit ---------------------- */
  function base64ToBlob(base64, contentType = 'image/jpeg') {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  const handleEdit = async (set, index) => {
    setIsLoadingForEdit(true);
    setEditingIndex(index);
    const itemName = set.web?.title || "";
    setImageName(itemName);

    const desktopUrl = set.raw?.imageUrlDesktop || set.raw?.imageUrl || set.web?.url || "";
    const tabletUrl = set.raw?.tabImageUrl || desktopUrl;
    const mobileUrl = set.raw?.mobileImageUrl || desktopUrl;

    setOriginalImages({ desktop: desktopUrl, tablet: tabletUrl, mobile: mobileUrl });
    setCroppedImages({ desktop: desktopUrl, tablet: tabletUrl, mobile: mobileUrl });
    setTempUrl(desktopUrl);
    setUploadedUrl(desktopUrl);

    if (!desktopUrl) {
      setIsLoadingForEdit(false);
      return;
    }

    // toast.info("Loading image for editing...");
    dispatch(
      getImageBase64Request(desktopUrl, ({ type, data, error }) => {
        if (type === 1 && data?.data) {
          const base64Data = data.data;

          // Convert Base64 to Blob
          const blob = base64ToBlob(base64Data, "image/jpeg");
          const blobUrl = URL.createObjectURL(blob);

          originalBlobRef.current = { blob, url: blobUrl };
          setSelectedImage({ url: blobUrl, previewOnly: false, isEditing: true });
          // toast.success("Image loaded and ready to crop!");
        } else {
                    setSelectedImage({ url: desktopUrl, previewOnly: true });
          toast.warning("Preview only. Cannot fetch image.");
        }
        setIsLoadingForEdit(false);

      })
    );;
  };


  /* ---------------------- Save Cropped Image ---------------------- */
  const handleSaveCrop = async () => {
    if (!selectedImage || !selectedImage.url) {
      toast.error("No image selected for cropping.");
      return;
    }

    if (!croppedAreaPixels) {
      toast.error("Please make a crop selection before saving.");
      return;
    }

    if (isCropping) {
      // toast.info("Already cropping...");
      return;
    }

    setIsCropping(true);

    try {
      // Use ImageBitmap API (NO CORS errors!)
      const { blob, url } = await getCroppedImg(selectedImage.url, croppedAreaPixels);


      // Upload cropped image as NEW image
      const filename = `gallery-${activeTab}-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: "image/jpeg" });
      const formData = new FormData();
      formData.append("file", file);

      dispatch(
        uploadAssestsV2Action({
          apiPayloadRequest: formData,
          uploadProgressId: `${activeTab}_crop_upload`,
          callback: (res) => {
            setIsCropping(false);

            if (res?.url) {
              setCroppedImages(prev => {
                const updated = {
                  ...prev,
                  [activeTab]: res.url,
                };

                // ✅ AUTO CLOSE MODAL AFTER MOBILE CROP
                if (activeTab === "mobile") {
                  if (editingIndex === null) {
                    // ✅ NEW IMAGE → AUTO CLOSE
                    setSelectedImage(null);
                    setActiveTab("desktop");
                  } else {
                    // ✅ EDIT MODE → KEEP MODAL OPEN
                    setActiveTab("desktop");
                  }

                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                  setCroppedAreaPixels(null);
                }
                 else {
                  setActiveTab(activeTab === "desktop" ? "tablet" : "mobile");
                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                  setCroppedAreaPixels(null);
                }

                return updated;
              });
            }
          }
          ,
        })
      );

    } catch (err) {
      setIsCropping(false);

      if (err.message.includes('CORS') || err.message.includes('tainted')) {
        toast.error("Cannot crop this image due to CORS. Please upload the image file directly.");
      } else {
        toast.error("Failed to crop image. Please try again.");
      }
    }
  };

  /* ---------------------- Re-edit Crops ---------------------- */
  const handleReEditCrops = async () => {
    if (!tempUrl) {
      toast.error("No image available for re-editing.");
      return;
    }

    try {
      // toast.info("Loading image for re-editing...");

      // If we already have a blob, use it
      if (originalBlobRef.current?.blob) {
        const blobUrl = URL.createObjectURL(originalBlobRef.current.blob);
        setSelectedImage({
          url: blobUrl,
          previewOnly: false
        });
        return;
      }

      // Otherwise load from URL
      const { blob, blobUrl } = await loadImageAsBlob(tempUrl);
      originalBlobRef.current = { blob, url: blobUrl };
      setSelectedImage({
        url: blobUrl,
        previewOnly: false
      });

      // toast.success("Image loaded for re-editing!");

    } catch (error) {
            toast.error("Cannot load image for editing. Please upload a new image file.");
      setSelectedImage({ url: tempUrl, previewOnly: true });
    }
  };


  /* ---------------------- Save/Update Image ---------------------- */
  const handleSave = () => {
    // Validate
    if (!imageName.trim()) {
      toast.error("Image name is required.");
      return;
    }

    if (!croppedImages.desktop) {
      toast.error("Please upload an image first.");
      return;
    }

    const { finalObjIdKey, finalObjIdValue } = getFinalIds();

    // Prepare data - use the croppedImages URLs (these are NEW URLs from re-cropping)
    const data = {
      [finalObjIdKey]: finalObjIdValue,
      imageName: imageName.trim(),
      imageUrl: croppedImages.desktop,
      imageUrlDesktop: croppedImages.desktop,
      imageUrlTablet: croppedImages.tablet || croppedImages.desktop,
      imageUrlMobile: croppedImages.mobile || croppedImages.desktop,

      url_link: croppedImages.desktop,
      imageUrlDesktop: croppedImages.desktop,
      tabImageUrl: croppedImages.tablet || croppedImages.desktop,
      mobileImageUrl: croppedImages.mobile || croppedImages.desktop,
    };

    // Editing existing
    if (editingIndex !== null && thumbUrls[editingIndex]) {
      const current = thumbUrls[editingIndex];
      data._id = current._id;


      dispatch(
        updateGalleryImageRequest(data, (res) => {
          if (res?.type === 1) {
            // toast.success("Image updated successfully with new crops!");
            resetForm();
          } else {
            toast.error(res?.error || "Update failed.");
          }
        })
      );
    } else {
      // Save new
      const saveData = {
        [finalObjIdKey]: finalObjIdValue,
        images: [{
          imageName: imageName.trim(),
          url_link: croppedImages.desktop,
          imageUrlDesktop: croppedImages.desktop,
          tabImageUrl: croppedImages.tablet || croppedImages.desktop,
          mobileImageUrl: croppedImages.mobile || croppedImages.desktop,
        }],
      };


      dispatch(
        saveGalleryRequest(saveData, (res) => {
          if (res?.type === 1) {
            toast.success("Image saved successfully!");
            resetForm();
          } else {
            toast.error(res?.error || "Save failed.");
          }
        })
      );
    }
  };

  /* ---------------------- Reset Form ---------------------- */
  const resetForm = () => {
    // Clean up blobs
    if (originalBlobRef.current?.url) {
      URL.revokeObjectURL(originalBlobRef.current.url);
    }

    if (selectedImage?.url && selectedImage.url.startsWith('blob:')) {
      URL.revokeObjectURL(selectedImage.url);
    }

    // Reset states
    setEditingIndex(null);
    setImageName("");
    setTempUrl("");
    setUploadedUrl("");
    setSelectedImage(null);
    setCroppedImages({ desktop: "", tablet: "", mobile: "" });
    setActiveTab("desktop");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setIsCropping(false);
    originalBlobRef.current = null;
    setResetUploadKey(prev => prev + 1);
  };

  /* ---------------------- Delete Image ---------------------- */
  const handleDelete = (index) => {
    const gallerySet = thumbUrls[index];
    const { finalObjIdKey, finalObjIdValue } = getFinalIds();

    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmToast
          title="Delete Image"
          message={`Are you sure you want to delete "${gallerySet.web?.title}"? This action cannot be undone.`}
          onClose={onClose}
          onSuccess={() => {
            // ✅ CLOSE DIALOG FIRST
            onClose();

            // ✅ THEN CALL API
            dispatch(
              deleteGalleryRequest(
                {
                  _id: gallerySet._id,
                  [finalObjIdKey]: finalObjIdValue,
                },
                (res) => {
                  if (res?.type === 1) {
                    toast.success("Image deleted successfully!", {
                      position: "top-right",
                      autoClose: 3000,
                    });
                    setResetUploadKey((prev) => prev + 1);
                  } else {
                    toast.error("Failed to delete image");
                  }
                }
              )
            );
          }}
        />
      ),
    });
  };



  /* ---------------------- Close Modal ---------------------- */
  const closeCropperModal = () => {
    setSelectedImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setIsCropping(false);
  };

  /* ---------------------- Crop Callback ---------------------- */
  const onCropComplete = useCallback((croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  /* ---------------------- Render Cropper ---------------------- */
  const renderCropper = () => {
    const aspect = activeTab === "desktop" ? 16 / 9 : activeTab === "tablet" ? 4 / 3 : 9 / 16;

    return (
      <div className="w-full max-w-[90vw] h-[60vh] md:h-[70vh] relative bg-black rounded-md overflow-hidden">
        <Cropper
          image={selectedImage.url}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          objectFit="contain"
          minZoom={0.5}
          maxZoom={3}
        />
      </div>
    );
  };

  /* ---------------------- JSX ---------------------- */
  return (
    <>
      {church ? (
        <div className="px-4 text-white">
          {/* Buttons */}
          <div className="flex justify-between items-center mb-3">
            {(tempUrl || editingIndex !== null) && (
              <button
                onClick={handleReEditCrops}
                className="flex  items-center  gap-2 text-yellowish border border-yellowish rounded-xl py-2 px-6 text-[12px] font-medium hover:bg-yellowish hover:text-black disabled:opacity-50"
              >
                <AiFillSetting /> Edit Crops
              </button>
            )}

            {!selectedImage && (
  <button
    onClick={handleSave}
    disabled={isCropping}
    className="text-yellowish border border-yellowish rounded-xl py-2 px-6 text-[12px] font-medium hover:bg-yellowish hover:text-black disabled:opacity-50"
  >
    Save Image
  </button>
)}

          </div>

          {/* Image Name */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Image Name *"
              value={imageName}
              onChange={(e) => setImageName(e.target.value)}
              className="bg-blackish p-2 w-full text-white text-[14px] rounded-md h-10"
              required
            />
            {/* {!imageName.trim() && (
              <p className="text-red-400 text-xs mt-1">Image name is required</p>
            )} */}
          </div>

          {/* Upload Controls */}
          <UploadControlsV2
            key={resetUploadKey}
            className="my-3"
            title="Gallery Image *"
            subtitle="Upload JPG or PNG only"
            showPreview={true}
            handleChangeUrl={handleUrlFieldChange}
            webUploadProgressId="galleryImageUpload"
            handleWebFileUpload={(e) => {
              const file = e.target.files?.[0];
              if (file) handleGalleryFileInputChange(file);
            }}
            webUploadUrl={tempUrl}
            handleWebPreview={() => {
              if (tempUrl) {
                setSelectedImage({ url: tempUrl, previewOnly: true });
              }
            }}
            isFieldRequired={false}
            showMobileUpload={false}
            showTabletUpload={false}
            showWebUpload={true}
            acceptedFileTypes=".jpg,.jpeg,.png"
          />


          {/* Gallery Grid */}
        {/* Gallery Grid */}
{thumbUrls.length > 0 && (
  <div className="flex flex-wrap gap-12 mt-6">
    {thumbUrls.map((set, index) => (
      <div key={set._id} className="relative group">
        <img
          src={set.web?.url || "/placeholder.jpg"}
          alt="Gallery"
          className="w-[180px] h-[120px] object-cover rounded-lg border border-gray-700 cursor-pointer transition-transform duration-300 hover:scale-[1.03]"
          onClick={() => setSelectedImage({ url: set.web?.url, previewOnly: true })}
        />

        {/* Tablet preview icon (top-right corner) */}
        <div className="absolute -top-8 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {set.tablet?.url && (
            <img
              src={set.tablet.url}
              title="Tablet Image"
              className="w-6 h-6 rounded-full border border-white shadow object-cover cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage({ url: set.tablet.url, previewOnly: true });
              }}
            />
          )}
        </div>

        {/* Mobile preview icon (right side) */}
        <div className="absolute top-2 -right-8 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {set.mobile?.url && (
            <img
              src={set.mobile.url}
              title="Mobile Image"
              className="w-6 h-6 rounded-full border border-white shadow object-cover cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage({ url: set.mobile.url, previewOnly: true });
              }}
            />
          )}
        </div>

        {/* Image title */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-[10px] px-2 py-0.5 rounded-md">
          {set.web.title}
        </div>

        {/* Edit button */}
        <div className="absolute bottom-2 left-2 bg-black/50 p-1 rounded-full cursor-pointer">
          <AiFillEdit
            className="text-white w-4 h-4"
            onClick={() => handleEdit(set, index)}
          />
        </div>

        {/* Delete button */}
        <div className="absolute top-2 left-2 bg-black/50 p-1 rounded-full cursor-pointer">
          <AiOutlineClose
            className="text-white w-4 h-4"
            onClick={() => handleDelete(index)}
          />
        </div>
      </div>
    ))}
  </div>
)}
{isLoadingForEdit && (
  <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <LoaderSpinner className="h-10 w-10 text-yellowish" />
      <p className="text-sm text-gray-200">Loading image for editing…</p>
    </div>
  </div>
)}

          {/* Modal */}
          {selectedImage && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] px-4">
              <button
                className="absolute top-6 right-6 bg-black/60 p-3 rounded-full hover:bg-black"
                onClick={closeCropperModal}
                disabled={isCropping}
              >
                <AiOutlineClose className="text-white w-6 h-6" />
              </button>

              {selectedImage.previewOnly ? (
                <div className="bg-gray-900 p-6 rounded-lg max-w-[1100px] w-full">
                  <div className="relative">
                    <div className="absolute top-0 right-0 cursor-pointer" onClick={closeCropperModal}>
                      <ImCross />
                    </div>
                    <img src={selectedImage.url} alt="preview" className="w-full h-auto max-h-[80vh] object-contain" />
                  </div>
                </div>
              ) : (
                <div className="bg-gray-900 p-6 rounded-lg max-w-[1100px] w-full">
                  <div className="flex justify-center mb-4 gap-3">
                    {["desktop", "tablet", "mobile"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => {
                          setActiveTab(tab);
                          setCrop({ x: 0, y: 0 });
                          setZoom(1);
                          setCroppedAreaPixels(null);
                        }}
                        className={`px-4 py-2 rounded-md text-sm ${
                          activeTab === tab ? "bg-yellowish text-black font-semibold" : "bg-gray-700 text-white"
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1 flex flex-col items-center">
                      {renderCropper()}

                      <div className="w-full max-w-[600px] mt-4">
                        <label className="block text-sm mb-1">Zoom: {zoom.toFixed(2)}</label>
                        <input
                          type="range"
                          min={0.5}
                          max={3}
                          step={0.01}
                          value={zoom}
                          onChange={(e) => setZoom(Number(e.target.value))}
                          className="w-full"
                          disabled={isCropping}
                        />
                      </div>

                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleSaveCrop}
                          disabled={!croppedAreaPixels || isCropping}
                          className="bg-yellowish text-black px-4 py-2 rounded-md font-semibold hover:bg-yellow-600 disabled:opacity-50"
                        >
                          {isCropping ? "Cropping..." : `Save ${activeTab} Crop`}
                        </button>


                      </div>
                    </div>

                    <div className="w-full md:w-[320px] flex flex-col gap-4">
                      <div className="text-sm text-gray-200 mb-2">Previews</div>
                      <div className="flex flex-col gap-3">
                        {["desktop", "tablet", "mobile"].map((tab) => (
                          <div key={tab} className="bg-black/40 p-2 rounded-md flex items-center gap-3">
                            <div className="w-20 h-12 bg-gray-800 rounded overflow-hidden flex items-center justify-center">
                              <img
                                src={croppedImages[tab] || (tab === activeTab ? selectedImage.url : "/placeholder.jpg")}
                                alt={`${tab}-preview`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 text-xs text-gray-100">
                              <div className="font-medium">{tab.charAt(0).toUpperCase() + tab.slice(1)}</div>
                              <div className="text-[11px] text-gray-300">
                                {croppedImages[tab] ? "✓ Uploaded" : "Not set"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {editingIndex !== null && (
                        <div className="text-xs text-yellow-300 mt-2">
                          <strong>Note:</strong> New crops will replace existing ones with fresh URLs.
                        </div>
                      )}
                                            {editingIndex !== null && (
  <div className="flex justify-end mt-6">
    <button
      onClick={handleSave}
      disabled={isCropping}
      className="bg-yellowish text-black px-6 py-2 rounded-md font-semibold hover:bg-yellow-600 disabled:opacity-50"
    >
      Update Image
    </button>
  </div>
)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <ReactModal isOpen={false}></ReactModal>
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400">
         <p>Add Church/Ministry/Channel First</p>

        </div>
      )}
    </>
  );
};

export default Galleryfield;